import React, { useState, useEffect } from "react";

import db from "../../../services/query";

import PrintHeader from "../common/PrintHeader";
import Img from "../../common/Img";
import { formatPrintMoney } from "../financePrintUtils";
import { toShamsiDate } from "../parity/AriaReport";
import {
  ReceiptAmountIcon,
  ReceiptCalendarIcon,
  ReceiptDocIcon,
  ReceiptUserIcon,
} from "../teacher-salary-report/_ReceiptDetailIcons";

const feeRows = (receipt) => [
  { label: "باقیات از گذشته", value: receipt?.past_balance },
  { label: "فیس سمستر جاری", value: receipt?.fee },
  { label: "تخفیف", value: receipt?.discount },
  { label: "بعد از کسر تخفیف", value: receipt?.payable },
  { label: "مبلغ دریافت شده", value: receipt?.amount, emphasis: true },
  { label: "باقی مانده سمستر", value: receipt?.balance },
  { label: "مجموع باقی مانده", value: receipt?.overall_balance, emphasis: true },
];

/** Normalize API payment_date to Gregorian YYYY-MM-DD when possible. */
const toGregorianIso = (raw) => {
  const value = String(raw || "").trim();
  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const slash = value.match(/^(\d{4})\/(\d{2})\/(\d{2})/);
  if (slash && Number(slash[1]) >= 1700) {
    return `${slash[1]}-${slash[2]}-${slash[3]}`;
  }
  return "";
};

const StudentFeeReceipt = ({ match }) => {
  const [receipt, setReceipt] = useState({});

  const printHeadTexts = [
    receipt?.office_name ?? "معاونیت امور محصلان",
    receipt?.faculty ?? "",
    receipt?.entry_year ?? "",
  ].filter(Boolean);

  const title = receipt?.title ?? "رسید فیس";
  const rawPaymentDate = String(receipt?.payment_date || "").trim();
  const gregorianIso = toGregorianIso(rawPaymentDate);
  const headerGregorian = gregorianIso || rawPaymentDate;
  const headerShamsi = gregorianIso ? toShamsiDate(gregorianIso) : "";

  useEffect(() => {
    requestData();
  }, []);

  const requestData = async () => {
    const { receipt_id } = match.params;
    try {
      await db
        .getAll(`students/fees/receipt?receiptId=${receipt_id}`)
        .then(({ data }) => {
          if (data?.data?.[0]) setReceipt(data.data[0]);
        });
    } catch ({ response }) {
      console.log(response);
    }
  };

  const detailRows = [
    {
      label: "تاریخ",
      value: headerGregorian,
      Icon: ReceiptCalendarIcon,
    },
    {
      label: "کد دانشجو",
      value: receipt?.aria_id ?? "",
      Icon: ReceiptUserIcon,
    },
    {
      label: "ريفرنس نمبر",
      value: receipt?.bank_ref_no ?? "",
      Icon: ReceiptDocIcon,
    },
    {
      label: "مبلغ",
      value:
        receipt?.amount != null && receipt?.amount !== ""
          ? formatPrintMoney(receipt.amount)
          : "",
      Icon: ReceiptAmountIcon,
      bold: true,
    },
  ];

  return (
    <main className="main-print-wrapper payment-receipt-print student-fee-receipt-print">
      <PrintHeader
        title={title}
        date={headerGregorian || " "}
        shamsiDate={headerShamsi || undefined}
        printHeadTexts={printHeadTexts}
        landscape
      />

      <div style={{ display: "grid", gap: "10px", marginBottom: "5px" }}>
        <div
          className="print-finance-top-row"
          style={{ display: "grid", gridTemplateColumns: "auto 55%", gap: "10px" }}
        >
          <div className="report-summary-area white-background-with-border student-fee-breakdown">
            <div className="student-fee-breakdown-list">
              {feeRows(receipt).map(({ label, value, emphasis }) => (
                <div
                  key={label}
                  className={`student-fee-breakdown-row${emphasis ? " is-emphasis" : ""}`}
                >
                  <span className="student-fee-breakdown-label">{label}</span>
                  <span className="student-fee-breakdown-value print-ltr-amount">
                    {formatPrintMoney(value)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="white-background-with-border print-finance-profile-panel">
            <div className="overall-summary">
              <div className="wrapper">
                <p>مجموع باقی مانده</p>
                <h2 className="print-ltr-amount">
                  {formatPrintMoney(receipt?.overall_balance)}
                </h2>
              </div>
            </div>
            <div className="print-finance-profile-row">
              <div className="std-profile rounded">
                <div className="wrapper">
                  <Img src={receipt?.student_photo} />
                </div>
              </div>
              <div className="print-finance-profile-meta">
                <div className="wrapper">
                  <h3>{receipt?.full_name ?? ""}</h3>
                  <p className="student-fee-code print-ltr-amount">
                    {receipt?.aria_id ? `کد دانشجو: ${receipt.aria_id}` : ""}
                  </p>
                  <p>{receipt?.student_father_name ?? ""}</p>
                  <p>{receipt?.class_desc ?? ""}</p>
                  <p>{receipt?.student_phone ?? ""}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="print-finance-details-row"
        style={{
          paddingTop: "20px",
          display: "grid",
          gridTemplateColumns: "40% auto",
          gap: "20px",
        }}
      >
        <div className="receipt-sub-card-container receipt-remark-wrap" style={{ height: "100%" }}>
          <div
            className="sub-card receipt-remark-card student-fee-bank-card"
            style={{
              height: "100%",
              display: "grid",
              gridTemplateColumns: "1fr auto",
              alignItems: "center",
              columnGap: "12px",
            }}
          >
            <div className="student-fee-bank-values">
              <p className="content print-ltr-amount">{receipt?.account ?? ""}</p>
              <p className="content print-ltr-amount">{receipt?.account_no ?? ""}</p>
            </div>
            <div className="student-fee-bank-labels">
              <p className="label">حساب</p>
              <p className="label">شماره حساب</p>
            </div>
          </div>
        </div>

        <div className="receipt-sub-card-container receipt-field-cards">
          {detailRows.map(({ label, value, Icon, bold }) => (
            <div
              key={label}
              className={`sub-card receipt-field-card${bold ? " bold" : ""}`}
            >
              <span className="receipt-detail-icon" aria-hidden="true">
                <Icon />
              </span>
              <p className="content">{value}</p>
              <p className="label">{label}</p>
            </div>
          ))}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "auto auto auto",
          paddingTop: "50px",
        }}
      >
        <div className="print-identity">
          <div className="print-signature-area">
            <div className="wrapper" style={{ width: "80%" }}>
              <hr />
              <p>نام و امضاء تایید کننده</p>
            </div>
          </div>
        </div>
        <div className="print-identity">
          <div className="print-signature-area">
            <div className="wrapper" style={{ width: "80%" }}>
              <hr />
              <p>نام و امضاء دریافت کننده</p>
            </div>
          </div>
        </div>
        <div className="print-identity">
          <div className="print-signature-area">
            <div className="wrapper" style={{ width: "80%" }}>
              <hr />
              <p>نام و امضاء محصل</p>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default StudentFeeReceipt;
